/**
 * Límites de carga de documentos. El API los aplica (multipart y `DocumentsService.store`);
 * las apps los usan para validar antes de enviar.
 */
export const DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;
/** Archivos por petición de carga. */
export const DOCUMENT_MAX_FILES = 10;
/** Tipos que el API guarda, con sus extensiones. */
export const DOCUMENT_TYPES = [
    { mime: 'application/pdf', ext: ['pdf'] },
    { mime: 'image/png', ext: ['png'] },
    { mime: 'image/jpeg', ext: ['jpg', 'jpeg'] },
    { mime: 'image/tiff', ext: ['tif', 'tiff'] },
    { mime: 'image/webp', ext: ['webp'] },
    { mime: 'application/msword', ext: ['doc'] },
    { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: ['docx'] },
    { mime: 'application/vnd.ms-excel', ext: ['xls'] },
    { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ext: ['xlsx'] },
    { mime: 'text/plain', ext: ['txt'] },
    { mime: 'message/rfc822', ext: ['eml'] },
    { mime: 'application/zip', ext: ['zip'] },
    { mime: 'application/vnd.rar', ext: ['rar'] },
];
/** Para los textos de ayuda de las zonas de carga. */
export const DOCUMENT_TYPES_LABEL = 'PDF, imágenes (JPG, PNG, TIFF, WEBP), Word, Excel, texto, correo (.eml), ZIP o RAR';
/** Valor para el atributo `accept`: tipos MIME y extensiones (el navegador a veces no informa el tipo). */
export const DOCUMENT_ACCEPT = [...DOCUMENT_TYPES.map((t) => t.mime), ...DOCUMENT_TYPES.flatMap((t) => t.ext.map((e) => `.${e}`))].join(',');
const ALLOWED = new Set(DOCUMENT_TYPES.map((t) => t.mime));
/** Sin firma binaria: se reconocen por la extensión (o el tipo declarado). */
const TEXT_LIKE = new Set(['text/plain', 'message/rfc822']);
/** Word y Excel antiguos son contenedores OLE: el contenido no dice cuál de los dos es. */
const OLE_BASED = new Set(['application/msword', 'application/vnd.ms-excel']);
/** docx y xlsx son ZIP por dentro. */
const ZIP_BASED = new Set(['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']);
const ALIASES = { 'application/x-rar-compressed': 'application/vnd.rar', 'image/jpg': 'image/jpeg' };
/** Bytes del comienzo del archivo que bastan para reconocer su tipo. */
export const DOCUMENT_SNIFF_BYTES = 16;
export function fileExtension(name) {
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}
const starts = (b, sig, at = 0) => b.length >= at + sig.length && sig.every((v, i) => b[at + i] === v);
/** Tipo por la firma binaria (magic bytes) de los formatos que se aceptan. Null si no tiene una conocida. */
export function sniffDocumentMime(head) {
    if (starts(head, [0x25, 0x50, 0x44, 0x46]))
        return 'application/pdf';
    if (starts(head, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
        return 'image/png';
    if (starts(head, [0xff, 0xd8, 0xff]))
        return 'image/jpeg';
    if (starts(head, [0x49, 0x49, 0x2a, 0x00]) || starts(head, [0x4d, 0x4d, 0x00, 0x2a]))
        return 'image/tiff';
    if (starts(head, [0x52, 0x49, 0x46, 0x46]) && starts(head, [0x57, 0x45, 0x42, 0x50], 8))
        return 'image/webp';
    if (starts(head, [0x50, 0x4b, 0x03, 0x04]))
        return 'application/zip';
    if (starts(head, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]))
        return 'application/x-cfb';
    if (starts(head, [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07]))
        return 'application/vnd.rar';
    return null;
}
/**
 * Tipo con el que se guarda un archivo, decidido por su contenido (nunca solo por la extensión), o null si no se
 * acepta. La misma regla corre en las apps antes de enviar y en el API al guardar, para que no discrepen.
 * `sniffed` es lo que dice el contenido: `sniffDocumentMime` en el navegador, `file-type` en el API.
 */
export function resolveDocumentMime(sniffed, fileName, declared) {
    const byExt = DOCUMENT_TYPES.find((t) => t.ext.includes(fileExtension(fileName)))?.mime;
    const decl = declared?.split(';')[0]?.trim().toLowerCase();
    const mime = sniffed ? ALIASES[sniffed] ?? sniffed : null;
    if (!mime) {
        if (byExt && TEXT_LIKE.has(byExt))
            return byExt;
        return decl && TEXT_LIKE.has(decl) ? decl : null;
    }
    if (mime === 'application/x-cfb')
        return byExt && OLE_BASED.has(byExt) ? byExt : null;
    if (mime === 'application/zip' && byExt && ZIP_BASED.has(byExt))
        return byExt;
    return ALLOWED.has(mime) ? mime : null;
}
/** Por qué un archivo no se puede adjuntar (vacío, muy grande, tipo no permitido), o null si sirve. Para las apps. */
export async function documentFileProblem(file) {
    if (file.size === 0)
        return 'Está vacío.';
    if (file.size > DOCUMENT_MAX_BYTES)
        return `Pesa más de ${DOCUMENT_MAX_BYTES / 1024 / 1024} MB.`;
    const head = new Uint8Array(await file.slice(0, DOCUMENT_SNIFF_BYTES).arrayBuffer());
    if (resolveDocumentMime(sniffDocumentMime(head), file.name, file.type))
        return null;
    const ext = fileExtension(file.name);
    return DOCUMENT_TYPES.some((t) => t.ext.includes(ext))
        ? `Su contenido no corresponde a un archivo .${ext}.`
        : `No es de un tipo permitido. Se aceptan ${DOCUMENT_TYPES_LABEL}.`;
}
/**
 * Anexos por enlace (`case_links`): lo que no cabe en `DOCUMENT_MAX_BYTES` o el ciudadano ya tiene en una nube
 * (Drive, OneDrive, WeTransfer…). Van en la lista de adjuntos del expediente pero no son documentos: no entran al
 * índice electrónico ni se descargan; la constancia de radicación (o el PDF del correo) los deja escritos.
 */
export const CASE_LINKS_MAX = 20;
export const CASE_LINK_MAX_LENGTH = 2000;
/** Por qué un texto no sirve como enlace (no es una dirección http/https completa), o null si sirve. */
export function caseLinkProblem(text) {
    const t = text.trim();
    if (!t)
        return 'Escribe el enlace.';
    if (t.length > CASE_LINK_MAX_LENGTH)
        return `El enlace tiene más de ${CASE_LINK_MAX_LENGTH} caracteres.`;
    let u;
    try {
        u = new URL(t);
    }
    catch {
        return 'No es un enlace completo: debe empezar por https:// o http://';
    }
    if (u.protocol !== 'https:' && u.protocol !== 'http:')
        return 'Solo se aceptan enlaces https:// o http://';
    if (!u.hostname.includes('.'))
        return 'El enlace no tiene un dominio válido.';
    return null;
}
/** Un enlace pegado sin el esquema («drive.google.com/…») se completa con https://. */
export function normalizeCaseLink(text) {
    const t = text.trim();
    return !t || /^[a-z][a-z0-9+.-]*:/i.test(t) ? t : `https://${t}`;
}
//# sourceMappingURL=document.js.map